import { getPatientEventsData } from "app-shell/NavBar/PatientAppointmentSearch/utils.ts";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useContext } from "react";

import { DateTime } from "@bps/date-time";
import {
  NoDataTile,
  ScrollablePane,
  SelectionMode,
  Stack,
  Tile,
  useFormContext,
  useResizeElementObserver
} from "@bps/fluent-ui";
import { RunQueryOptions } from "@libs/utils/promise-observable/promise-observable.types.ts";
import { QueryResult } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import {
  ClinicalReminderManagementFilter,
  ClinicalReminderManagementFilterValues
} from "@modules/clinical/screens/patient-record/components/clinical-reminder/ClinicalReminderManagementFilterBase.tsx";
import { ClinicalRemindersTable } from "@modules/clinical/screens/patient-record/components/clinical-reminder/ClinicalRemindersTable.tsx";
import { ClinicalActivityTableRow } from "@modules/clinical/screens/patient-record/components/clinical-reminder/types/clinical-activity-table.type.ts";
import { ClinicalActivityStatus } from "@shared-types/clinical/clinical-activity-status.type.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";

import { InboxScreenContext } from "../../context/InboxScreenContext.ts";

export interface RemindersClinicalProps {
  setSelected?: (selected: ClinicalActivityTableRow[]) => void;
  showAsFollowUp?: boolean;
}
const RemindersClinicalListBase: FC<RemindersClinicalProps> = observer(
  props => {
    const {
      state: { values: filter }
    } = useFormContext<ClinicalReminderManagementFilterValues>();

    const { setSelectedClinicalReminderRows } = useContext(InboxScreenContext);

    const { clinical, practice, booking, comms } = useStores();
    const { setSelected } = props;

    const getItems = useCallback(
      async (
        options?: RunQueryOptions
      ): Promise<QueryResult<ClinicalActivityTableRow>> => {
        const reminderPageStatuses = [
          ClinicalActivityStatus.New,
          ClinicalActivityStatus.InProgress,
          ClinicalActivityStatus.NoLongerRequired,
          ClinicalActivityStatus.Declined,
          ClinicalActivityStatus.NoResponse
        ];

        const useWeekFilter = filter.useAppointmentRange;

        const clinicalReminders = await clinical.getClinicalReminders({
          ...options,
          ...filter,
          remainingVisits: filter.remainingVisits,
          startDateTime: DateTime.fromJSDate(filter.dueDateStartDate)?.toISO(),
          endDateTime: DateTime.fromJSDate(filter.dueDateEndDate)?.toISO(),
          providerIds: filter.providerIds,
          statuses: props.showAsFollowUp ? undefined : reminderPageStatuses,
          reasons: filter.reasons,
          patientIds: filter.patientIds,
          urgent: filter.urgent,
          clinicallySignificant: filter.clinicallySignificant,
          withNoRecordEntries: !props.showAsFollowUp,
          withRecordEntriesOnly: props.showAsFollowUp,
          activePatientsOnly: filter.activePatients ?? undefined,
          reminderCommStatuses: props.showAsFollowUp
            ? filter.reminderCommStatuses
            : undefined,
          sentStartDateTime: DateTime.fromJSDate(
            filter.sentStartDateTime
          )?.toISO(),
          sentEndDateTime: DateTime.fromJSDate(filter.sentEndDateTime)?.toISO(),
          usePreferenceBlacklist: useWeekFilter
        });

        if (clinicalReminders.results.length === 0)
          return { skip: 0, take: 25, results: [] };

        const patientIds = Array.from(
          new Set(clinicalReminders.results.map(activity => activity.patientId))
        );

        const [patients] = await Promise.all([
          practice.getContactsById(patientIds),
          comms.getContactPreferences(patientIds)
        ]);

        const contactEvents =
          await booking.ui.getPatientNextAppointments(patientIds);

        const patientEventsData = getPatientEventsData(contactEvents, patients);

        const nextAppointmentMap = new Map<string, CalendarEvent | undefined>();
        for (const patientData of patientEventsData) {
          if (patientData.upcomingAppointment) {
            nextAppointmentMap.set(
              patientData.patient.id,
              patientData.upcomingAppointment
            );
          } else {
            nextAppointmentMap.set(patientData.patient.id, undefined);
          }
        }

        // Get all of administration reminders by args.
        const activityIds = clinicalReminders.results.map(
          activity => activity.id
        );

        const reminderComms = await clinical.getClinicalReminderComms({
          activityIds
        });

        const remindersWithPatients = clinicalReminders.results.map(
          (activity): ClinicalActivityTableRow => {
            const patient = practice.contactsMap.get(activity.patientId);

            const nextAppointment =
              nextAppointmentMap.get(activity.patientId) || undefined;

            const clinicalReminderComm = reminderComms.find(
              x => x.clinicalActivityId === activity.id
            );

            let disableDueToAppointmentRange: boolean = false;

            if (clinical.clinicalActivitiesPreference?.useReminderMinRange) {
              const patientAppointment = patientEventsData.find(
                x =>
                  x.upcomingAppointment && x.patient.id === activity.patientId
              );

              if (patientAppointment?.upcomingAppointment?.startDateTime) {
                const endTime = DateTime.today()
                  .plus({
                    week: clinical.clinicalActivitiesPreference
                      ?.reminderMinWeekRange
                  })
                  .endOf("day")
                  .toISO();

                disableDueToAppointmentRange =
                  patientAppointment.upcomingAppointment.startDateTime <=
                  DateTime.fromISO(endTime);
              }
            }

            return {
              id:
                activity.id +
                (clinicalReminderComm?.contactRecords.length ?? "x"), // The list will only refresh the items if the id has changed. As we want to update the item when they're successfully sent out, include the value in the Id.
              activity,
              patient,
              nextAppointment,
              clinicalReminderComm,
              withinAppointmentRange: disableDueToAppointmentRange ?? false
            };
          }
        );

        return { ...clinicalReminders, results: remindersWithPatients };
      },
      [clinical, filter, props.showAsFollowUp, practice, booking, comms]
    );

    return (
      <ScrollablePane
        styles={{
          root: {
            height: "100%",
            padding: 8,
            position: "relative"
          }
        }}
      >
        <ClinicalRemindersTable
          showAsFollowUp={props.showAsFollowUp}
          isMultiline={true}
          setSelected={rows => {
            setSelectedClinicalReminderRows(rows);
            if (setSelected) {
              setSelected(rows);
            }
          }}
        >
          {({ selection, columns, renderRow, renderDetailsHeader }) => (
            <InfiniteScrollList
              setKey="clinical-reminders-list"
              selectionMode={SelectionMode.multiple}
              stickyHeader
              cellAlignment="start"
              getItems={getItems}
              columns={columns}
              onRenderRow={renderRow}
              onRenderDetailsHeader={renderDetailsHeader}
              selection={selection.current}
              selectionPreservedOnEmptyClick={true}
              refreshKey={clinical.clinicalActivityRefreshKey}
              onRenderNoResults={() => (
                <NoDataTile
                  textProps={{ text: "No information to display" }}
                  linkProps={{ hidden: true }}
                  showBoxShadow={false}
                />
              )}
            />
          )}
        </ClinicalRemindersTable>
      </ScrollablePane>
    );
  }
);

const FilteredRemindersClinicalList: FC<RemindersClinicalProps> = props => {
  const { setElement, resizeObserverEntry, element } =
    useResizeElementObserver();

  const panelWidth = resizeObserverEntry
    ? resizeObserverEntry.borderBoxSize[0].inlineSize
    : 0;

  return (
    <Tile
      styles={{
        root: {
          flexGrow: 1,
          height: "100%",
          position: "relative"
        }
      }}
    >
      <Stack
        styles={{
          root: { height: "100%", overflowX: "auto" }
        }}
      >
        <ClinicalReminderManagementFilter
          isFollowUp={props.showAsFollowUp}
          isShort={panelWidth <= 1200}
        >
          <div
            ref={r => {
              if (r && !element) {
                setElement(r);
              }
            }}
            style={{ height: "100%" }}
          >
            <RemindersClinicalListBase {...props} />
          </div>
        </ClinicalReminderManagementFilter>
      </Stack>
    </Tile>
  );
};

export const RemindersClinicalList = withFetch(
  x => [
    x.clinical.loadActivityDescriptions(),
    x.clinical.getClinicalActivitiesPreferenceByTenant()
  ],
  FilteredRemindersClinicalList
);
